import { useNotifications } from '@revenuewell/uc-notifications-client';
import { FormSubmissionEvent } from '@revenuewell/uc-notifications-client/dist/types/notifications';
import { useHistory } from 'apps/wrapper/src/hooks/use-history-intercept';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useProductsByLocation } from 'apps/wrapper/src/hooks/use-products-by-location';
import { useSettings } from 'apps/wrapper/src/hooks/use-settings';
import { useFrames } from 'apps/wrapper/src/hooks/use-wrapper-frames';
import { ReactNode, useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/use-notification/use-notification';
import {
  LocationSwitchEventData,
  SwitchLocationDialog
} from '../../shared/dialog/switch-location-dialog/switch-location-dialog';

interface FormsNotificationEventListenerProps {
  children: ReactNode;
}

export const FormsNotificationEventListener: React.FC<FormsNotificationEventListenerProps> = ({ children }) => {
  const { listen } = useNotifications();
  const { allFramesLoaded } = useFrames();
  const { showNotification } = useNotification();
  const { forms2Submission } = useSettings();
  const { selectedLocation, locations, switchLocation, selectedLocationWithStatus } = useLocations();
  const history = useHistory();
  const [locationSwitchEvent, setLocationSwitchEvent] = useState<LocationSwitchEventData | null>(null);
  const locationId = selectedLocation?.id;
  const { isLoading } = useProductsByLocation(locationId);

  const [storedFormIdAndLocationId, setStoredFormIdAndLocationId] = useState<{
    formId: string;
    locationId: number;
  } | null>(null);

  const goToForms = (formId: string, locationIdSelected?: number) => {
    setLocationSwitchEvent(null);
    setStoredFormIdAndLocationId(null);
    if (history.location.pathname.includes(`/forms/submissions/index/${formId}`)) {
      return;
    }

    history.push({
      pathname: `/forms/submissions/index/${formId}`,
      search: locationIdSelected ? `?locationId=${locationIdSelected}` : ''
    });
  };

  useEffect(() => {
    const { pathname, search } = history.location;
    const formSubmissionPath = '/forms/submissions/index/';
    const searchParams = new URLSearchParams(search);
    const locationIdFromUrl = searchParams.get('preferredLocationId');

    if (pathname.startsWith(formSubmissionPath) && locationIdFromUrl) {
      const formId = pathname.replace(formSubmissionPath, '');
      setStoredFormIdAndLocationId({ formId, locationId: parseInt(locationIdFromUrl) });
    }
  }, []);

  useEffect(() => {
    if (allFramesLoaded && selectedLocationWithStatus?.status === 'Complete' && storedFormIdAndLocationId) {
      const locationWithStored = locations.find((l) => Number(l.id) === Number(storedFormIdAndLocationId.locationId));

      if (locationWithStored && Number(locationWithStored.id) !== Number(selectedLocationWithStatus.location.id)) {
        setLocationSwitchEvent({
          locationId: locationWithStored.id,
          locationName: locationWithStored.name,
          patientName: '',
          patientPhone: '',
          extraData: { formId: storedFormIdAndLocationId.formId }
        });
      } else if (locationWithStored) {
        goToForms(storedFormIdAndLocationId.formId);
      }
    }
  }, [allFramesLoaded, selectedLocationWithStatus?.status, locations, storedFormIdAndLocationId]);

  useEffect(
    () =>
      listen('form-submission', (event) => {
        if (!forms2Submission) {
          return;
        }

        const { data } = event as FormSubmissionEvent;
        const locationIdFromFormEvent = (event as any).locationid as number;

        if ('id' in data) {
          const onClick = async () => {
            if (Number(locationIdFromFormEvent) !== Number(locationId)) {
              setLocationSwitchEvent({
                locationId: locationIdFromFormEvent as number,
                locationName: locations.find((l) => l.id === locationIdFromFormEvent)?.name || '',
                patientName: `${data.patientFirstName} ${data.patientLastName}`,
                patientPhone: '',
                extraData: { formId: data.id }
              });
            } else {
              goToForms(data.id);
            }
          };

          showNotification(
            'New Form Submission',
            { body: `Form ${data.formName} from ${data.patientFirstName} ${data.patientLastName} has been submitted` },
            onClick
          );
        }
      }),
    [listen, locationId, forms2Submission, locations]
  );

  const handleLocationSwitch = async () => {
    if (locationSwitchEvent) {
      setLocationSwitchEvent(null);
      setStoredFormIdAndLocationId(null);
      await switchLocation(locationSwitchEvent.locationId);
      goToForms(locationSwitchEvent.extraData.formId, locationSwitchEvent.locationId);
    }
  };

  return (
    <>
      {children}
      {locationSwitchEvent && !isLoading && (
        <SwitchLocationDialog
          eventData={locationSwitchEvent}
          message="This form is from a patient in another location. Would you like to switch locations and view the form?"
          onSwitchLocation={handleLocationSwitch}
          onClose={() => {
            setLocationSwitchEvent(null);
            setStoredFormIdAndLocationId(null);
          }}
        />
      )}
    </>
  );
};
